import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AppProvider, useApp } from "@/context/AppContext";
import Layout from "@/components/Layout";
import { Toaster } from "@/components/ui/sonner";
import { PageSkeleton } from "@/components/Shared";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordReset";

// Marketing pages (code-split)
const HowItWorks = lazy(() => import("@/pages/HowItWorks"));
const Platform = lazy(() => import("@/pages/Platform"));
const SecurityPage = lazy(() => import("@/pages/SecurityPage"));
const BookDemo = lazy(() => import("@/pages/BookDemo"));
const Deck = lazy(() => import("@/pages/Deck"));
const Pitch = lazy(() => import("@/pages/Pitch"));
const Compare = lazy(() => import("@/pages/Compare"));

// App pages (code-split for <2s perceived startup)
const Today = lazy(() => import("@/pages/Today"));
const KolList = lazy(() => import("@/pages/KolList"));
const KolProfile = lazy(() => import("@/pages/KolProfile"));
const MeetingBriefing = lazy(() => import("@/pages/MeetingBriefing"));
const Capture = lazy(() => import("@/pages/Capture"));
const Memory = lazy(() => import("@/pages/Memory"));
const CommandCenter = lazy(() => import("@/pages/CommandCenter"));
const CommandDecisions = lazy(() => import("@/pages/CommandDecisions"));
const CommandSignals = lazy(() => import("@/pages/CommandSignals"));
const CommandOpportunities = lazy(() => import("@/pages/CommandOpportunities"));
const Team = lazy(() => import("@/pages/Team"));
const MslProfile = lazy(() => import("@/pages/MslProfile"));
const Compliance = lazy(() => import("@/pages/Compliance"));
const FollowUps = lazy(() => import("@/pages/FollowUps"));
const Impact = lazy(() => import("@/pages/Impact"));
const Graph = lazy(() => import("@/pages/Graph"));
const Governance = lazy(() => import("@/pages/Governance"));
const Workspace = lazy(() => import("@/pages/Workspace"));
const Uploads = lazy(() => import("@/pages/Uploads"));
const Reports = lazy(() => import("@/pages/Reports"));
const AdminAnalytics = lazy(() => import("@/pages/AdminAnalytics"));
const Audit = lazy(() => import("@/pages/Audit"));

function ProtectedShell() {
  const { user } = useApp();
  if (user === undefined) {
    return <div className="max-w-6xl mx-auto px-5 md:px-10 py-16"><PageSkeleton /></div>;
  }
  if (user === null) return <Navigate to="/login" replace />;
  return (
    <Layout>
      <Suspense fallback={<PageSkeleton />}>
        <Routes>
          <Route path="/today" element={<Today />} />
          <Route path="/kols" element={<KolList />} />
          <Route path="/kols/:id" element={<KolProfile />} />
          <Route path="/briefing/:id" element={<MeetingBriefing />} />
          <Route path="/capture" element={<Capture />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/memory" element={<Memory />} />
          <Route path="/command" element={<CommandCenter />} />
          <Route path="/decisions" element={<CommandDecisions />} />
          <Route path="/signals" element={<CommandSignals />} />
          <Route path="/opportunities" element={<CommandOpportunities />} />
          <Route path="/team" element={<Team />} />
          <Route path="/team/:slug" element={<MslProfile />} />
          <Route path="/intelligence" element={<Navigate to="/command" replace />} />
          <Route path="/executive" element={<Navigate to="/command" replace />} />
          <Route path="/compliance" element={<Compliance />} />
          <Route path="/followups" element={<FollowUps />} />
          <Route path="/impact" element={<Impact />} />
          <Route path="/graph" element={<Graph />} />
          <Route path="/governance" element={<Governance />} />
          <Route path="/admin" element={<Workspace />} />
          <Route path="/admin/analytics" element={<AdminAnalytics />} />
          <Route path="/uploads" element={<Uploads />} />
          <Route path="/audit" element={<Audit />} />
        </Routes>
      </Suspense>
    </Layout>
  );
}

function App() {
  return (
    <div className="App">
      <AppProvider>
        <BrowserRouter>
          <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/platform" element={<Platform />} />
              <Route path="/security" element={<SecurityPage />} />
              <Route path="/book-demo" element={<BookDemo />} />
              <Route path="/deck" element={<Deck />} />
              <Route path="/pitch" element={<Pitch />} />
              <Route path="/compare" element={<Compare />} />
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="*" element={<ProtectedShell />} />
            </Routes>
          </Suspense>
          <Toaster position="top-center" />
        </BrowserRouter>
      </AppProvider>
    </div>
  );
}

export default App;
